<template>
  <div class="kbr" :title="titleTip">
    <div class="kbr-track">
      <div class="kbr-track-bg" :class="trackBgClass" />
      <div
        class="kbr-fill"
        :class="fillClass"
        :style="{width: currentPct + '%'}"
      />
      <!-- Budget band (low → high) -->
      <div v-if="showBand" class="kbr-band" :style="bandRegionStyle" />
      <!-- Calendar month elapsed: linear “on pace” mark on the same 0→max hours axis -->
      <div
        v-if="showMonthProgressLine"
        class="kbr-month-progress"
        :style="{left: monthProgressLinePct + '%'}"
        :title="
          'Calendar: ' + Math.round(monthElapsedPercent) + '% through month'
        "
      />
      <!-- Single target tick (when no low/high band) -->
      <div
        v-if="showTargetTick"
        class="kbr-marker kbr-marker-target"
        :style="{left: targetPct + '%'}"
      >
        <span class="kbr-marker-cap">T</span>
      </div>
      <!-- Low / high ticks -->
      <div
        v-if="lowPct != null"
        class="kbr-marker kbr-marker-bound"
        :style="{left: lowPct + '%'}"
      >
        <span class="kbr-marker-cap">L</span>
      </div>
      <div
        v-if="highPct != null"
        class="kbr-marker kbr-marker-bound"
        :style="{left: highPct + '%'}"
      >
        <span class="kbr-marker-cap">H</span>
      </div>
      <!-- Current position -->
      <div class="kbr-current-wrap" :style="{left: currentPct + '%'}">
        <div class="kbr-current-dot" :class="dotClass" />
      </div>
    </div>
    <div class="kbr-scale caption grey--text text--darken-1">
      <span class="kbr-scale-num kbr-scale-left">0</span>
      <span
        v-if="lowDecimal != null && lowDecimal > 0"
        class="kbr-scale-num"
        :style="{left: lowPct + '%'}"
      >
        {{ fmtAxis(lowDecimal) }}
      </span>
      <span
        v-if="highDecimal != null && highDecimal > 0"
        class="kbr-scale-num"
        :style="{left: highPct + '%'}"
      >
        {{ fmtAxis(highDecimal) }}
      </span>
      <span
        v-if="showTargetTick && targetDecimal != null"
        class="kbr-scale-num"
        :style="{left: targetPct + '%'}"
      >
        {{ fmtAxis(targetDecimal) }}
      </span>
      <span class="kbr-scale-num kbr-scale-right">{{ fmtAxis(scaleMax) }}</span>
    </div>
  </div>
</template>

<script>
function decimalToHHMM(hoursDecimal) {
  const safe = Number.isFinite(hoursDecimal) ? Math.max(hoursDecimal, 0) : 0;
  const totalMinutes = Math.round(safe * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

export default {
  name: "KonekoBudgetRangeBar",
  props: {
    /** Company-wide (or scoped) hours — dot position */
    currentDecimal: {
      type: Number,
      default: 0,
    },
    /** Vue 2: type Number rejects null — use validator so optional bounds stay null */
    lowDecimal: {
      default: null,
      validator: (v) => v === null || v === undefined || typeof v === "number",
    },
    highDecimal: {
      default: null,
      validator: (v) => v === null || v === undefined || typeof v === "number",
    },
    targetDecimal: {
      default: null,
      validator: (v) => v === null || v === undefined || typeof v === "number",
    },
    /** 0–100: vertical line at that share of the bar width. -1 = hidden. */
    monthElapsedPercent: {
      type: Number,
      default: -1,
    },
    /**
     * '', 'good' (healthy: past low, under cap, on/behind pace),
     * 'pace' (ahead of calendar line), 'over' (past personal max).
     */
    status: {
      type: String,
      default: "",
    },
  },
  computed: {
    dotClass() {
      if (this.status === "over") return "kbr-current-dot--over";
      if (this.status === "pace") return "kbr-current-dot--pace";
      if (this.status === "good") return "kbr-current-dot--good";
      return "";
    },
    trackBgClass() {
      if (this.status === "over") return "kbr-track-bg--over";
      if (this.status === "pace") return "kbr-track-bg--pace";
      if (this.status === "good") return "kbr-track-bg--good";
      return "";
    },
    fillClass() {
      if (this.status === "over") return "kbr-fill--over";
      if (this.status === "pace") return "kbr-fill--pace";
      if (this.status === "good") return "kbr-fill--good";
      return "kbr-fill--ok";
    },
    showMonthProgressLine() {
      const p = this.monthElapsedPercent;
      return typeof p === "number" && Number.isFinite(p) && p >= 0 && p <= 100;
    },
    monthProgressLinePct() {
      if (!this.showMonthProgressLine) return 0;
      return Math.min(100, Math.max(0, this.monthElapsedPercent));
    },
    scaleMax() {
      const parts = [
        this.currentDecimal,
        this.lowDecimal,
        this.highDecimal,
        this.targetDecimal,
      ].filter((x) => x != null && Number.isFinite(x) && x >= 0);
      const peak = parts.length ? Math.max(...parts) : 0;
      return peak <= 0 ? 1 : peak * 1.12;
    },
    showBand() {
      const lo = this.lowDecimal;
      const hi = this.highDecimal;
      return lo != null && hi != null && hi > lo && lo >= 0;
    },
    showTargetTick() {
      if (this.showBand) return false;
      return this.targetDecimal != null && this.targetDecimal > 0;
    },
    currentPct() {
      const p = this.positionPct(this.currentDecimal);
      return p == null ? 0 : p;
    },
    lowPct() {
      return this.positionPct(this.lowDecimal);
    },
    highPct() {
      return this.positionPct(this.highDecimal);
    },
    targetPct() {
      return this.positionPct(this.targetDecimal);
    },
    bandRegionStyle() {
      if (!this.showBand) return {};
      const a = this.lowPct;
      const b = this.highPct;
      if (a == null || b == null || b <= a) return {};
      return {
        left: `${a}%`,
        width: `${b - a}%`,
      };
    },
    titleTip() {
      const cur = decimalToHHMM(this.currentDecimal || 0);
      let tip = `Current (month): ${cur} · scale max ${decimalToHHMM(
        this.scaleMax
      )}`;
      if (this.showMonthProgressLine) {
        tip += ` · calendar ${Math.round(
          this.monthElapsedPercent
        )}% (dark red line)`;
      }
      if (this.status === "pace") {
        tip += " · ahead of month pace (amber)";
      }
      if (this.status === "over") {
        tip += " · over max (red)";
      }
      return tip;
    },
  },
  methods: {
    /** Whole hours under the axis only (drops `:mm` for readability). */
    fmtAxis(dec) {
      if (!Number.isFinite(dec)) return "";
      const s = decimalToHHMM(dec);
      const h = parseInt(String(s).split(":")[0], 10);
      return Number.isFinite(h) ? String(h) : "";
    },
    positionPct(n) {
      if (n == null || !Number.isFinite(n)) return null;
      return Math.min(100, Math.max(0, (n / this.scaleMax) * 100));
    },
  },
};
</script>

<style scoped>
.kbr {
  min-width: 140px;
  max-width: 320px;
}

.kbr-track {
  position: relative;
  height: 18px;
  margin-bottom: 2px;
}

.kbr-track-bg {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 8px;
  margin-top: -4px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.08);
}

.kbr-fill {
  position: absolute;
  left: 0;
  top: 50%;
  height: 8px;
  margin-top: -4px;
  border-radius: 4px 0 0 4px;
  pointer-events: none;
  z-index: 0;
}

.kbr-fill--ok {
  background: rgba(33, 150, 243, 0.32);
}

.kbr-fill--good {
  background: rgba(46, 125, 50, 0.42);
}

.kbr-fill--pace {
  background: rgba(255, 143, 0, 0.55);
}

.kbr-fill--over {
  background: rgba(198, 40, 40, 0.55);
}

.kbr-band {
  position: absolute;
  top: 50%;
  margin-top: -4px;
  height: 8px;
  border-radius: 4px;
  background: rgba(33, 150, 243, 0.22);
  pointer-events: none;
}

.kbr-month-progress {
  position: absolute;
  top: 50%;
  left: 0;
  width: 2px;
  height: 18px;
  margin-top: -9px;
  border-radius: 1px;
  background: #7f1d1d;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.7);
  transform: translateX(-50%);
  z-index: 1;
  pointer-events: none;
}

.kbr-marker {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  pointer-events: none;
}

.kbr-marker-bound {
  width: 2px;
  height: 14px;
  border-radius: 1px;
  background: rgba(245, 124, 0, 0.95);
}

.kbr-marker-target {
  width: 2px;
  height: 14px;
  border-radius: 1px;
  background: rgba(106, 27, 154, 0.9);
}

.kbr-marker-cap {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translate(-50%, -2px);
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
}

.kbr-current-wrap {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  pointer-events: none;
}

.kbr-current-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #1976d2;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

.kbr-current-dot--good {
  background: #2e7d32;
}

.kbr-current-dot--pace {
  background: #ff8f00;
}

.kbr-current-dot--over {
  background: #c62828;
}

.kbr-track-bg--good {
  background: rgba(46, 125, 50, 0.16);
}

.kbr-track-bg--pace {
  background: rgba(255, 143, 0, 0.2);
}

.kbr-track-bg--over {
  background: rgba(198, 40, 40, 0.22);
}

.kbr-scale {
  position: relative;
  height: 16px;
  margin-top: 2px;
}

.kbr-scale-num {
  position: absolute;
  transform: translateX(-50%);
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
}

.kbr-scale-left {
  left: 0;
  transform: translateX(0);
}

.kbr-scale-right {
  left: 100%;
  transform: translateX(-100%);
}
</style>
